import { useEffect, useRef, useState } from "react";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";

type ChatMsg = { role: "user" | "assistant"; content: string };
type Resp = { response: string; suggestions: string[]; run_id: string };

const QUICK = [
  { label: "Top-5 KPIs für CFO", msg: "Welche 5 KPIs sind für mich als CFO essenziell?", mode: "kpis", persona: "CFO" },
  { label: "ROI-Skizze 100k€", msg: "Rechne einen ROI für 100k€ Investment in 12 Monaten.", mode: "roi", persona: "CEO" },
  { label: "Prozessschritt planen", msg: "Plane den nächsten KPI-Mining-Prozessschritt mit Agenten, Quality-Gates, Evidence und HITL.", mode: "strategy", persona: "Berater" },
  { label: "Strategie-Sparring", msg: "Stress-Test meine Markt-Annahmen für die nächsten 24 Monate.", mode: "strategy", persona: "CEO" },
];

export function AdvisorDrawer() {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("kpis");
  const [persona, setPersona] = useState("CEO");
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "/") { e.preventDefault(); setOpen((o) => !o); }
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [history]);

  async function send(text: string, qMode?: string, qPersona?: string) {
    const m = (qMode || mode) as any;
    const p = (qPersona || persona) as any;
    setMode(m); setPersona(p);
    if (!text.trim() || sending) return;
    setSending(true);
    const next: ChatMsg[] = [...history, { role: "user", content: text }];
    setHistory(next);
    try {
      const r = await api.post<Resp>("/advisor/chat", { message: text, mode: m, persona: p, history: next.slice(-8) });
      setHistory([...next, { role: "assistant", content: r.response }]);
      setInput("");
    } catch (e) {
      setHistory([...next, { role: "assistant", content: `⚠️ ${String(e)}` }]);
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <button className="drawer-fab" title="KI-Berater (⌘/)" onClick={() => setOpen((o) => !o)}>
        <span className="drawer-fab-dot"></span> KI-Berater
      </button>
      <div className={`drawer ${open ? "open" : ""}`} role="dialog" aria-hidden={!open}>
        <header className="drawer-head">
          <div>
            <strong>KI-Berater</strong>
            <p className="muted small">{persona} · {mode} · Tenant <code>{effectiveTenantId}</code></p>
          </div>
          <button className="btn ghost" onClick={() => setOpen(false)}>×</button>
        </header>
        {history.length === 0 && (
          <div className="drawer-empty">
            <p className="muted">Schnellstart:</p>
            {QUICK.map((q) => (
              <button key={q.label} className="suggestion-chip" onClick={() => send(q.msg, q.mode, q.persona)} disabled={sending}>{q.label}</button>
            ))}
          </div>
        )}
        <div className="drawer-chat" ref={scrollRef}>
          {history.map((m, i) => (
            <div key={i} className={`chat-bubble ${m.role}`}>
              <div className="chat-meta">{m.role === "user" ? "Sie" : `KI-Berater · ${persona}/${mode}`}</div>
              <div className="chat-content">{m.content}</div>
            </div>
          ))}
        </div>
        <form className="drawer-input" onSubmit={(e) => { e.preventDefault(); send(input); }}>
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Frage stellen … (⌘/ schließen)" disabled={sending} />
          <button type="submit" className="btn primary" disabled={sending || !input.trim()}>{sending ? "…" : "Senden"}</button>
        </form>
      </div>
    </>
  );
}
